import { useMemo } from 'react';

import type { SymbolKey } from '@/entities/symbol';
import PreviewFullscreenChart from '@/features/DataHub/components/Preview/ChartPreviews/PreviewFullscreenChart';
import PreviewFwdCurveChart from '@/features/DataHub/components/Preview/ChartPreviews/PreviewFwdCurveChart';
import PreviewSeasonalChart from '@/features/DataHub/components/Preview/ChartPreviews/PreviewSeasonalChart';
import PreviewSpotChart from '@/features/DataHub/components/Preview/ChartPreviews/PreviewSpotChart';
import { useExportMeta } from '@/features/DataHub/modules/Export/context/exportContexts';
import { ExportTypes } from '@/features/DataHub/modules/Export/types';
import { SELECTOR_MODES } from '@/features/SymbolSelector/store/symbolSelector.slice';
import { useAppSelector } from '@/hooks/redux-hooks';

const toCurveSymbol = (s: SymbolKey): SymbolKey => ({ ...s, Period: '' });

const dedupByCurve = (symbols: SymbolKey[]): SymbolKey[] => {
  const seen = new Set<string>();
  return symbols.reduce<SymbolKey[]>((acc, s) => {
    const key = `${s.Code}|${s.PeriodType}|${s.TimeRef}|${s.transactionDate ?? ''}`;
    if (!seen.has(key)) {
      seen.add(key);
      acc.push(toCurveSymbol(s));
    }
    return acc;
  }, []);
};

const Preview = ({ showLegend = true }: { showLegend?: boolean }) => {
  const { type: exportType, from, isLightMode } = useAppSelector(state => state.export);
  const selectorMode = useAppSelector(state => state.symbolSelector.selectorMode);
  const { uniqueSymbols } = useExportMeta();
  const symbols = useMemo(() => Array.from(uniqueSymbols), [uniqueSymbols]);
  const curveSymbols = useMemo(() => dedupByCurve(symbols), [symbols]);

  if (selectorMode === SELECTOR_MODES.FORWARD_CURVE) {
    return (
      <PreviewFwdCurveChart
        symbolLimit={100}
        symbols={curveSymbols}
      />
    );
  }

  if (!exportType || exportType === ExportTypes.DATA) {
    return (
      <PreviewSpotChart
        showLegend={showLegend}
        symbolLimit={6}
        symbols={symbols}
      />
    );
  }
  if (exportType === 'fullscreen') {
    return (
      <PreviewFullscreenChart
        isLightMode={isLightMode}
        showLegend={showLegend}
        symbols={symbols}
      />
    );
  }
  if (exportType === 'spot') {
    return (
      <PreviewSpotChart
        showLegend={showLegend}
        symbolLimit={100}
        symbols={symbols}
      />
    );
  }
  if (exportType === 'forwardSimple') {
    return (
      <PreviewFwdCurveChart
        symbolLimit={100}
        symbols={symbols}
      />
    );
  }
  if (exportType === 'seasonal') {
    return (
      <PreviewSeasonalChart
        from={from}
        symbols={symbols}
      />
    );
  }
  return null;
};
export default Preview;
